/**
 * Macro #20 — "Command Console" (Admin), route `#admin`.
 *
 * Server-authoritative: the presence of this screen is gated by
 * `useAdminCapability`, but that is NOT the security boundary — every admin API
 * independently enforces `require_admin` (SIWE session + env allowlist). A direct
 * `#admin` visit by a non-admin fails closed here AND at the API.
 *
 * The console hosts two instruments under one shell (native to The Grid —
 * cosmic-dark ground, chamfer/HUD panels, mono vocabulary, restrained glow):
 *   • Review Operations — discover & review NEW / REPUBLISH public publications
 *     (Review Queue, Clients, embedded Territory Inspector).
 *   • Moderation — the previously approved case ledger / dossier.
 * A live Review Queue pending counter is always visible in the header + rail
 * (lightweight refresh, no realtime infra).
 */
import { useEffect, useState } from "react";
import { useAdminCapability } from "../useAdminCapability";
import { useReviewSummary } from "./useReviewSummary";
import { AdminModerationView } from "./AdminModerationView";
import { AdminReviewOperations } from "./AdminReviewOperations";
import { AdminRewardsView } from "./AdminRewardsView";
import { useRewardsDashboard } from "./useRewardsDashboard";
import { cn } from "../cn";

type NavKey = "queue" | "clients" | "moderation" | "rewards";

export function AdminConsoleRoute() {
  const { isAdmin, loading: capLoading } = useAdminCapability();
  const [nav, setNav] = useState<NavKey>("queue");
  const { summary, refresh } = useReviewSummary(isAdmin);
  const { dashboard: rewards, refresh: refreshRewards } = useRewardsDashboard(isAdmin);
  const [hideOpen, setHideOpen] = useState(false);
  useEffect(() => {
    if (nav !== "moderation") setHideOpen(false);
  }, [nav]);

  if (capLoading) {
    return (
      <Shell>
        <div data-testid="admin-loading" className="mx-auto mt-24 w-[min(520px,90%)] rounded-lg border border-border-soft bg-panel p-8 text-center font-mono text-[13px] text-text-muted">
          Verifying access…
        </div>
      </Shell>
    );
  }
  if (!isAdmin) {
    return (
      <Shell>
        <div data-testid="admin-forbidden" className="mx-auto mt-24 w-[min(560px,90%)] panel-chamfer panel-chamfer--sm p-8">
          <div className="font-display text-[16px] font-semibold text-neon-coral">Access Restricted</div>
          <p className="mt-2 text-[12px] text-text-secondary leading-relaxed">
            The Command Console is available to authorized administrators only. Your session
            must be signed and carry moderator authority.
          </p>
          <a
            data-testid="admin-forbidden-back"
            href="#grid"
            className="inline-flex items-center gap-2 h-7 px-3 no-underline font-mono text-[10px] uppercase tracking-[0.16em] text-text-primary neon-glass pill-ghost"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden><path d="M8.5 3.5L5 7l3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Back to Grid
          </a>
        </div>
      </Shell>
    );
  }

  const pending = summary?.pending ?? null;

  return (
    <Shell>
      <div className="mx-auto w-[min(1560px,100%)] px-5 md:px-8 py-6">
        {/* Header — title, inline nav tabs, and Back to Grid. The nav lives in
            the header (not a left rail) so the workspace keeps full width for
            the queue + inspector. */}
        <header className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-3">
            <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: "var(--neon-coral)", boxShadow: "0 0 10px var(--neon-coral-glow)" }} />
            <div>
              <h1 data-testid="admin-console-title" className="m-0 font-display text-[22px] font-semibold tracking-[0.01em] text-text-primary">
                Command Console
              </h1>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.28em] text-text-muted">
                Review · Moderation · Admin
              </p>
            </div>
          </div>

          <nav data-testid="admin-nav" className="flex flex-wrap items-center gap-1.5">
            <NavItem testid="admin-nav-queue" label="Review Queue" active={nav === "queue"} onClick={() => setNav("queue")} count={pending} />
            <NavItem testid="admin-nav-clients" label="Clients" active={nav === "clients"} onClick={() => setNav("clients")} />
            <NavItem testid="admin-nav-moderation" label="Moderation" active={nav === "moderation"} onClick={() => setNav("moderation")} />
            <NavItem testid="admin-nav-rewards" label="Rewards" active={nav === "rewards"} onClick={() => setNav("rewards")} count={rewards ? rewards.payout_requests.length : null} />
          </nav>

          <div className="ml-auto flex items-center gap-2.5">
            {nav === "moderation" && (
              <button
                type="button"
                data-testid="admin-new-moderation"
                onClick={() => setHideOpen(true)}
                className="facet-btn facet-btn--coral h-7 px-3 text-[10px] tracking-[0.16em]"
              >
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden><path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
                New Moderation
              </button>
            )}
            <a
              data-testid="admin-back-to-grid"
              href="#grid"
              className="inline-flex items-center gap-2 h-7 px-3 no-underline font-mono text-[10px] uppercase tracking-[0.16em] text-text-primary neon-glass pill-ghost"
            >
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden><path d="M8.5 3.5L5 7l3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Back to Grid
            </a>
          </div>
        </header>

        {/* Workspace — full width */}
        <div data-testid="admin-workspace" className="mt-6">
          {nav === "moderation" ? (
            <AdminModerationView onMutated={refresh} hideOpen={hideOpen} onHideOpenChange={setHideOpen} />
          ) : nav === "rewards" ? (
            <AdminRewardsView dashboard={rewards} onRefresh={refreshRewards} />
          ) : (
            <AdminReviewOperations key={nav} view={nav} onDataChanged={refresh} />
          )}
        </div>
      </div>
    </Shell>
  );
}

function NavItem({
  testid,
  label,
  active,
  onClick,
  count,
}: {
  testid: string;
  label: string;
  active: boolean;
  onClick: () => void;
  count?: number | null;
}) {
  const showCount = count !== undefined;
  return (
    <button
      type="button"
      data-testid={testid}
      data-active={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 h-8 px-3.5 rounded-md font-mono text-[10px] uppercase tracking-[0.16em] transition-colors",
        active
          ? "text-text-primary bg-[color:var(--spec-cyan-soft)] border border-border-accent"
          : "text-text-secondary border border-border-soft hover:text-text-primary hover:bg-white/[0.03]",
      )}
    >
      <span>{label}</span>
      {showCount && (
        <span
          data-testid="admin-pending-count"
          className="inline-flex items-center justify-center min-w-[1.35rem] h-4 px-1 rounded-sm text-[9px] font-semibold tabular-nums"
          style={(count ?? 0) > 0 ? { color: "#f0b429", border: "1px solid rgba(240,180,41,0.5)" } : { color: "var(--text-muted)", border: "1px solid var(--border-soft, rgba(255,255,255,0.12))" }}
        >
          {count ?? "…"}
        </span>
      )}
    </button>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div data-testid="admin-console-route" className="w-full h-full overflow-y-auto bg-obsidian text-text-primary">
      {children}
    </div>
  );
}
